import React, { useState } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { useT } from '../i18n/useT';

interface Message {
  id: number;
  userId: number;
  userName: string;
  userAvatar: string;
  userImage?: any;
  content?: string;
  type: 'text' | 'options' | 'choice_response' | 'text_input';
  options?: string[];
  selectedOption?: number;
  timestamp: string;
  isQuestion?: boolean;
  allowTextInput?: boolean;
  responses?: Array<{
    userId: number;
    userName: string;
    userAvatar: string;
    userImage?: any;
    selectedOption?: number;
    textResponse?: string;
    timestamp: string;
  }>;
}

export default function CardDiscussionScreen() {
  const t = useT();
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const card = route.params?.card;
  const onBack = () => navigation.goBack();

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      userId: 1,
      userName: 'Sarah Johnson',
      userAvatar: 'SJ',
      userImage: require('../../shared/assets/anna.jpg'),
      content: `Great content on ${card?.title || 'this mission'}! I have a question about implementation.`,
      type: 'text',
      timestamp: '2024-01-15T10:30:00Z'
    },
    {
      id: 2,
      userId: 1,
      userName: 'Sarah Johnson',
      userAvatar: 'SJ',
      userImage: require('../../shared/assets/anna.jpg'),
      content: 'Which approach do you think works best for beginners?',
      type: 'options',
      options: [
        'Start with bodyweight exercises',
        'Begin with light weights immediately',
        'Focus on cardio first',
        'Mix everything from day one'
      ],
      timestamp: '2024-01-15T10:31:00Z',
      isQuestion: true,
      responses: [
        {
          userId: 2,
          userName: 'John Doe',
          userAvatar: 'JD',
          userImage: require('../../shared/assets/raoul.jpg'),
          selectedOption: 0,
          timestamp: '2024-01-15T10:45:00Z'
        },
        {
          userId: 3,
          userName: 'Alex Chen',
          userAvatar: 'AC',
          userImage: require('../../shared/assets/youssef.jpg'),
          selectedOption: 0,
          timestamp: '2024-01-15T10:47:00Z'
        },
        {
          userId: 4,
          userName: 'Emma Wilson',
          userAvatar: 'EW',
          userImage: require('../../shared/assets/nina.jpg'),
          selectedOption: 3,
          timestamp: '2024-01-15T11:15:00Z'
        }
      ]
    },
    {
      id: 3,
      userId: 1,
      userName: 'Sarah Johnson',
      userAvatar: 'SJ',
      userImage: require('../../shared/assets/anna.jpg'),
      content: 'Thanks for the responses! Now tell me about your experience:',
      type: 'text',
      timestamp: '2024-01-15T11:30:00Z'
    },
    {
      id: 4,
      userId: 1,
      userName: 'Sarah Johnson',
      userAvatar: 'SJ',
      userImage: require('../../shared/assets/anna.jpg'),
      content: 'What\'s your biggest challenge with staying consistent?',
      type: 'text_input',
      timestamp: '2024-01-15T11:31:00Z',
      isQuestion: true,
      allowTextInput: true,
      responses: [
        {
          userId: 3,
          userName: 'Alex Chen',
          userAvatar: 'AC',
          userImage: require('../../shared/assets/youssef.jpg'),
          textResponse: 'Finding time in my busy schedule is the hardest part.',
          timestamp: '2024-01-15T11:45:00Z'
        }
      ]
    }
  ]);

  const [activeResponses, setActiveResponses] = useState<{ [messageId: number]: { option?: number; text?: string } }>({});

  const participants = [
    { id: 1, name: 'Sarah Johnson', avatar: 'SJ', role: 'Mentor', online: true, image: require('../../shared/assets/anna.jpg') },
    { id: 2, name: 'John Doe', avatar: 'JD', role: 'You', online: true, image: require('../../shared/assets/raoul.jpg') },
    { id: 3, name: 'Alex Chen', avatar: 'AC', role: 'Teammate', online: true, image: require('../../shared/assets/youssef.jpg') },
    { id: 4, name: 'Emma Wilson', avatar: 'EW', role: 'Teammate', online: false, image: require('../../shared/assets/nina.jpg') }
  ];

  const currentUserId = 2; // John Doe (Raoul)

  const handleOptionSelect = (messageId: number, optionIndex: number) => {
    setActiveResponses(prev => ({
      ...prev,
      [messageId]: { option: optionIndex }
    }));
  };

  const handleTextChange = (messageId: number, text: string) => {
    setActiveResponses(prev => ({
      ...prev,
      [messageId]: { text }
    }));
  };

  const handleSubmitResponse = (messageId: number) => {
    const response = activeResponses[messageId];
    if (!response || (response.option === undefined && !response.text?.trim())) return;

    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId) {
        const newResponse = {
          userId: currentUserId,
          userName: 'John Doe',
          userAvatar: 'JD',
          userImage: require('../../shared/assets/raoul.jpg'),
          selectedOption: response.option,
          textResponse: response.text,
          timestamp: new Date().toISOString()
        };

        return {
          ...msg,
          responses: [...(msg.responses || []), newResponse]
        };
      }
      return msg;
    }));

    // Clear the active response
    setActiveResponses(prev => {
      const newResponses = { ...prev };
      delete newResponses[messageId];
      return newResponses;
    });
  };

  const hasUserResponded = (messageId: number) => {
    const message = messages.find(msg => msg.id === messageId);
    return message?.responses?.some(response => response.userId === currentUserId);
  };

  const getUserResponse = (messageId: number) => {
    const message = messages.find(msg => msg.id === messageId);
    return message?.responses?.find(response => response.userId === currentUserId);
  };

  const getOptionVotes = (message: Message) => {
    if (!message.options) return [];

    const votes = message.options.map((option, index) => ({
      option,
      index,
      count: 0,
      users: [] as string[]
    }));

    message.responses?.forEach(response => {
      if (response.selectedOption !== undefined && votes[response.selectedOption]) {
        votes[response.selectedOption].count++;
        votes[response.selectedOption].users.push(response.userName);
      }
    });

    return votes;
  };

  const renderMessage = (message: Message) => {
    const isCurrentUser = message.userId === currentUserId;
    const userResponse = getUserResponse(message.id);
    const hasResponded = hasUserResponded(message.id);
    const activeResponse = activeResponses[message.id];

    return (
      <View key={message.id} style={styles.messageContainer}>
        <View style={[styles.messageRow, isCurrentUser && styles.messageRowReverse]}>
          {message.userImage ? (
            <Image source={message.userImage} style={styles.messageAvatar} />
          ) : (
            <View style={styles.messageAvatar}>
              <Text style={styles.messageAvatarText}>{message.userAvatar}</Text>
            </View>
          )}
          
          <View style={[styles.messageContent, isCurrentUser && styles.messageContentReverse]}>
            <View style={[styles.messageHeader, isCurrentUser && styles.messageHeaderReverse]}>
              <Text style={styles.messageSender}>
                {isCurrentUser ? 'You' : message.userName}
              </Text>
              <Text style={styles.messageTime}>
                {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </View>
            
            {message.type === 'text' && (
              <View style={[styles.messageBubble, isCurrentUser ? styles.myMessageBubble : styles.otherMessageBubble]}>
                <Text style={[styles.messageText, isCurrentUser && styles.myMessageText]}>{message.content}</Text>
              </View>
            )}

            {(message.type === 'options' || message.type === 'text_input') && message.isQuestion && (
              <View style={styles.questionContainer}>
                <View style={[styles.messageBubble, styles.questionBubble]}>
                  <Text style={styles.questionText}>{message.content}</Text>
                  
                  {/* Show existing responses */}
                  {message.responses && message.responses.length > 0 && (
                    <View style={styles.responsesContainer}>
                      <Text style={styles.responsesTitle}>{t('cardChat.responses')}</Text>
                      
                      {message.type === 'options' && (
                        <View style={styles.optionsVotes}>
                          {getOptionVotes(message).map((vote) => (
                            <View key={vote.index} style={styles.voteRow}>
                              <Text style={styles.voteOption}>{vote.option}</Text>
                              <View style={styles.voteInfo}>
                                <View style={styles.voteAvatars}>
                                  {vote.users.slice(0, 3).map((userName, idx) => {
                                    const user = participants.find(p => p.name === userName);
                                    return (
                                      <View key={idx} style={styles.voteAvatar}>
                                        {user?.image ? (
                                          <Image source={user.image} style={styles.voteAvatarImage} />
                                        ) : (
                                          <Text style={styles.voteAvatarText}>{user?.avatar || userName[0]}</Text>
                                        )}
                                      </View>
                                    );
                                  })}
                                  {vote.users.length > 3 && (
                                    <View style={styles.voteAvatarMore}>
                                      <Text style={styles.voteAvatarMoreText}>+{vote.users.length - 3}</Text>
                                    </View>
                                  )}
                                </View>
                                <View style={styles.voteBadge}>
                                  <Text style={styles.voteBadgeText}>{vote.count}</Text>
                                </View>
                              </View>
                            </View>
                          ))}
                        </View>
                      )}

                      {message.type === 'text_input' && (
                        <View style={styles.textResponses}>
                          {message.responses.map((response, idx) => (
                            <View key={idx} style={styles.textResponse}>
                              {response.userImage ? (
                                <Image source={response.userImage} style={styles.responseAvatar} />
                              ) : (
                                <View style={styles.responseAvatar}>
                                  <Text style={styles.responseAvatarText}>{response.userAvatar}</Text>
                                </View>
                              )}
                              <View style={styles.responseContent}>
                                <Text style={styles.responseUserName}>{response.userName}:</Text>
                                <Text style={styles.responseText}>{response.textResponse}</Text>
                              </View>
                            </View>
                          ))}
                        </View>
                      )}
                    </View>
                  )}

                  {/* Show interaction area if user hasn't responded */}
                  {!hasResponded && (
                    <View style={styles.interactionArea}>
                      {message.type === 'options' && message.options && (
                        <View style={styles.optionsContainer}>
                          {message.options.map((option, index) => (
                            <TouchableOpacity
                              key={index}
                              style={[
                                styles.optionButton,
                                activeResponse?.option === index && styles.selectedOptionButton
                              ]}
                              onPress={() => handleOptionSelect(message.id, index)}
                            >
                              <View style={styles.optionContent}>
                                <View style={[
                                  styles.optionRadio,
                                  activeResponse?.option === index && styles.selectedOptionRadio
                                ]}>
                                  {activeResponse?.option === index && (
                                    <View style={styles.optionRadioDot} />
                                  )}
                                </View>
                                <Text style={styles.optionText}>{option}</Text>
                              </View>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}

                      {message.type === 'text_input' && (
                        <View style={styles.textInputContainer}>
                          <TextInput
                            style={styles.textInput}
                            placeholder={t('cardChat.placeholder')}
                            value={activeResponse?.text || ''}
                            onChangeText={(text) => handleTextChange(message.id, text)}
                            multiline
                          />
                        </View>
                      )}

                      {activeResponse && (activeResponse.option !== undefined || activeResponse.text?.trim()) && (
                        <TouchableOpacity 
                          style={styles.submitButton}
                          onPress={() => handleSubmitResponse(message.id)}
                        >
                          <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                          <Text style={styles.submitButtonText}>{t('cardChat.submit')}</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  )}

                  {/* Show user's response if they have responded */}
                  {hasResponded && userResponse && (
                    <View style={styles.userResponseContainer}>
                      <View style={styles.userResponseContent}>
                        <Ionicons name="checkmark-circle" size={16} color="#10B981" />
                        <Text style={styles.userResponseLabel}>{t('cardChat.youResponded')}</Text>
                        <Text style={styles.userResponseText}>
                          {userResponse.selectedOption !== undefined && message.options 
                            ? message.options[userResponse.selectedOption]
                            : userResponse.textResponse
                          }
                        </Text>
                      </View>
                    </View>
                  )}
                </View>
              </View>
            )}
          </View>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity onPress={onBack}>
            <Ionicons name="arrow-back" size={24} color="#374151" />
          </TouchableOpacity>
          
          <View style={styles.headerInfo}>
            <View style={styles.headerAvatar}>
              <Text style={styles.headerAvatarText}>{card?.author?.avatar || 'JD'}</Text>
            </View>
            <View style={styles.headerText}>
              <Text style={styles.headerTitle}>Discussion: {card?.title || 'Mission Discussion'}</Text>
              <Text style={styles.headerSubtitle}>{participants.length} participants</Text>
            </View>
          </View>
          
          <TouchableOpacity style={styles.participantsButton}>
            <Ionicons name="people" size={16} color="#374151" />
          </TouchableOpacity>
        </View>

        {/* Participants */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.participantsContainer}>
          {participants.map(participant => (
            <View key={participant.id} style={styles.participantChip}>
              <View style={styles.participantAvatar}>
                {participant.image ? (
                  <Image source={participant.image} style={styles.participantAvatarImage} />
                ) : (
                  <Text style={styles.participantAvatarText}>{participant.avatar}</Text>
                )}
                {participant.online && <View style={styles.onlineIndicator} />}
              </View>
              <Text style={styles.participantName}>
                {participant.role === 'You' ? 'You' : participant.name.split(' ')[0]}
              </Text>
            </View>
          ))}
        </ScrollView>
      </View>

      {/* Messages */}
      <ScrollView style={styles.messagesContainer}>
        <View style={styles.messagesContent}>
          {messages.map(renderMessage)}
        </View>
      </ScrollView>

      {/* Info Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          {t('cardChat.hint')}
        </Text>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  header: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginLeft: 12,
  },
  headerAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerAvatarText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  headerText: {
    marginLeft: 12,
    flex: 1,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#111827',
  },
  headerSubtitle: {
    fontSize: 12,
    color: '#6B7280',
  },
  participantsButton: {
    padding: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 6,
  },
  participantsContainer: {
    marginTop: 8,
  },
  participantChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    marginRight: 8,
  },
  participantAvatar: {
    position: 'relative',
    marginRight: 4,
  },
  participantAvatarImage: {
    width: 20,
    height: 20,
    borderRadius: 10,
  },
  participantAvatarText: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: 20,
  },
  onlineIndicator: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 8,
    height: 8,
    backgroundColor: '#10B981',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#FFFFFF',
  },
  participantName: {
    fontSize: 12,
    color: '#374151',
  },
  messagesContainer: {
    flex: 1,
  },
  messagesContent: {
    padding: 16,
  },
  messageContainer: {
    marginBottom: 24,
  },
  messageRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  messageRowReverse: {
    flexDirection: 'row-reverse',
  },
  messageAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 8,
  },
  messageAvatarText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  messageContent: {
    flex: 1,
    maxWidth: '80%',
  },
  messageContentReverse: {
    alignItems: 'flex-end',
  },
  messageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  messageHeaderReverse: {
    flexDirection: 'row-reverse',
  },
  messageSender: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  messageTime: {
    fontSize: 10,
    color: '#9CA3AF',
  },
  messageBubble: {
    borderRadius: 12,
    padding: 12,
  },
  myMessageBubble: {
    backgroundColor: '#3B82F6',
  },
  otherMessageBubble: {
    backgroundColor: '#F3F4F6',
  },
  questionBubble: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  messageText: {
    fontSize: 14,
    color: '#111827',
  },
  myMessageText: {
    color: '#FFFFFF',
  },
  questionText: {
    fontSize: 14,
    color: '#111827',
    marginBottom: 12,
  },
  questionContainer: {
    marginTop: 8,
  },
  responsesContainer: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 12,
    marginTop: 12,
  },
  responsesTitle: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  optionsVotes: {
    gap: 8,
  },
  voteRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  voteOption: {
    fontSize: 14,
    color: '#374151',
    flex: 1,
  },
  voteInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  voteAvatars: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  voteAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -4,
  },
  voteAvatarImage: {
    width: 20,
    height: 20,
    borderRadius: 10,
  },
  voteAvatarText: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: 'bold',
  },
  voteAvatarMore: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -4,
  },
  voteAvatarMoreText: {
    fontSize: 8,
    color: '#6B7280',
  },
  voteBadge: {
    backgroundColor: '#F3F4F6',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  voteBadgeText: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '600',
  },
  textResponses: {
    gap: 8,
  },
  textResponse: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  responseAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  responseAvatarText: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: 'bold',
  },
  responseContent: {
    flex: 1,
  },
  responseUserName: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  responseText: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  interactionArea: {
    marginTop: 12,
    gap: 12,
  },
  optionsContainer: {
    gap: 8,
  },
  optionButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
  },
  selectedOptionButton: {
    backgroundColor: '#3B82F6',
    borderColor: '#3B82F6',
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  optionRadio: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedOptionRadio: {
    borderColor: '#FFFFFF',
  },
  optionRadioDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#FFFFFF',
  },
  optionText: {
    fontSize: 14,
    color: '#374151',
    flex: 1,
  },
  textInputContainer: {
    marginTop: 8,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  submitButton: {
    backgroundColor: '#3B82F6',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  userResponseContainer: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 12,
    marginTop: 12,
  },
  userResponseContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  userResponseLabel: {
    fontSize: 12,
    color: '#6B7280',
  },
  userResponseText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  footer: {
    padding: 12,
    backgroundColor: '#F3F4F6',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  footerText: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
}); 